import { atom, useAtomValue, useSetAtom } from 'jotai'
import { useCallback } from 'react'
import type { RestaurantTagId } from '~/lib/graphql/scalars'
import { draftSearchCriteria$ } from './draft'
import { type Facet, addPricePreferences, seatPreferences } from './facet'
import { createSelectedOthers } from './facet/selected'
import { toPriceLabel } from './price'
import { draftSelectedFoods$, draftSelectedScenes$, draftTagIds$ } from './tags'
import type { SearchCriteria } from './validate'

function countSelectedSeats(criteria?: SearchCriteria): number {
  return Object.values(seatPreferences).filter((p) => p.selected(criteria)).length
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest
  describe('countSelectedSeats', () => {
    it('ホテル外で小学生以上、窓際の個室テラス席、駐車場とカード支払いあり', () => {
      const criteria: SearchCriteria = {
        hotelRestaurant: false,
        windowSide: true,
        seatTypes: ['TERRACE', 'PRIVATE_ROOM', 'SEMI_PRIVATE_ROOM'],
        childUses: ['GRADE_SCHOOLERS'],
        smokingAllowed: false,
        parkingAvailable: true,
        cardPaymentAvailable: true,
      }
      expect(countSelectedSeats(criteria)).toBe(8)
    })
  })
}

function countSelectedPlans(criteria: SearchCriteria | undefined, tagIds: RestaurantTagId[]) {
  const set = new Set(tagIds)
  return [
    // 飲み放題は選択されている (true) のときだけ
    criteria?.freeFlow ? 1 : 0,
    set.has('2941' as RestaurantTagId) ? 1 : 0, // 3時間以上飲み放題
    // ブッフェは選択されたときだけを count
    criteria?.buffet ? 1 : 0,
    set.has('2981' as RestaurantTagId) ? 1 : 0, // アフタヌーンティー
  ].reduce((a, e) => a + e, 0)
}

const draftSelectedSeats$ = atom((get) => {
  const criteria = get(draftSearchCriteria$)
  return countSelectedSeats(criteria)
})
draftSelectedSeats$.debugLabel = 'draftSelectedPlanCriteria$'

const draftSelectedPlans$ = atom((get) => {
  const tagIds = get(draftTagIds$).sceneTagIds
  const criteria = get(draftSearchCriteria$)
  return countSelectedPlans(criteria, tagIds)
})
draftSelectedPlans$.debugLabel = 'draftSelectedPlanCriteria$'

const draftSelectedCounts$ = atom((get) => {
  return {
    scene: get(draftSelectedScenes$),
    seat: get(draftSelectedSeats$),
    plan: get(draftSelectedPlans$),
    food: get(draftSelectedFoods$),
    // 利用時に型が失われるので key は string でよい
    // @see OtherFacets.tsx の `Object.entries(buttons)`
  } as Record<string, number>
})
draftSelectedCounts$.debugLabel = 'draftSelectedCounts$'

export function useDraftSelectedCounts() {
  return useAtomValue(draftSelectedCounts$)
}

const currentPreferences$ = atom((get) => {
  const criteria = get(draftSearchCriteria$)
  const selected = get(selected$)
  return currentPreferences(selected, criteria)
})

const selected$ = createSelectedOthers(draftSearchCriteria$, 'preferences')

const clearPreferences$ = atom(null, (get, set) => {
  const selected = get(selected$)
  set(draftSearchCriteria$, (criteria) => clearPreferences(selected, criteria))
})

export function useCurrentPreferences() {
  return useAtomValue(currentPreferences$)
}

export function useClearPreferences() {
  const set = useSetAtom(clearPreferences$)
  return useCallback(() => {
    set()
  }, [set])
}

export function currentPreferences(
  selected: Facet[],
  criteria: SearchCriteria | undefined
): string[] {
  if (criteria === undefined) {
    return []
  }
  return Object.values(addPriceRange(selected, criteria))
    .filter((p) => p.showInList(criteria))
    .filter((l) => l !== undefined)
    .map((l) => l.label)
}

function addPriceRange(preferences: readonly Facet[], criteria?: SearchCriteria): readonly Facet[] {
  if (criteria === undefined) {
    return preferences
  }
  const label = toPriceLabel(criteria)
  if (label === undefined) {
    return preferences
  }
  return [
    {
      label,
      facetKey: '',
      // 使わないから固定値でよい
      selected: () => true,
      showInList: () => true,
      add: (current) => current ?? {},
      remove: (current) => {
        const { lowerBoundPrice, upperBoundPrice, ...rest } = current ?? {}
        return rest
      },
    },
    ...preferences,
  ]
}

function clearPreferences(selected: Facet[], criteria: undefined | SearchCriteria): SearchCriteria {
  return Object.values(addPricePreferences(selected, criteria)).reduce(
    (result, { remove }) => remove(result),
    criteria ?? {}
  )
}
